import * as React from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

const trapStack: HTMLElement[] = [];

// `offsetParent` is always null under jsdom, which would leave the trap inert
const isFocusable = (element: HTMLElement) =>
  !element.hasAttribute("hidden") &&
  element.getAttribute("aria-hidden") !== "true";

export function useFocusTrap(container: HTMLElement | null, active: boolean) {
  React.useEffect(() => {
    if (!active || !container) return;

    trapStack.push(container);

    const getFocusableItems = () =>
      Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      ).filter(isFocusable);

    if (!container.contains(document.activeElement)) {
      const [firstItem] = getFocusableItems();
      (firstItem ?? container).focus();
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      if (trapStack[trapStack.length - 1] !== container) return;

      const items = getFocusableItems();
      const first = items[0];
      const last = items[items.length - 1];

      if (!first || !last) {
        e.preventDefault();
        return;
      }

      const { activeElement } = document;

      const wrapTarget = () => {
        if (!container.contains(activeElement))
          return e.shiftKey ? last : first;
        if (e.shiftKey && activeElement === first) return last;
        if (!e.shiftKey && activeElement === last) return first;
      };

      const target = wrapTarget();
      if (!target) return;

      e.preventDefault();
      target.focus();
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      const index = trapStack.indexOf(container);
      if (index !== -1) trapStack.splice(index, 1);
    };
  }, [active, container]);
}
